import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import {
  DEFAULT_FLAG_VALUES,
  FEATURE_FLAG_KEYS,
  getLaunchDarklyClientId,
  isAllowedPath,
} from '../config/featureFlags.js';

const STORAGE_KEY = 'cv.flagOverrides';
const USER_KEY = 'cv.ldUserKey';

const FeatureFlagContext = createContext({
  flags: DEFAULT_FLAG_VALUES,
  ready: true,
  overrides: {},
  setOverride: () => {},
  resetOverride: () => {},
});

export function FeatureFlagProvider({ children }) {
  const clientId = getLaunchDarklyClientId();
  const [overrides, setOverrides] = usePersistentOverrides();

  const baseValue = useMemo(
    () => buildFlagContext(DEFAULT_FLAG_VALUES, null, overrides, true, setOverrides),
    [overrides, setOverrides],
  );

  if (!clientId) {
    return <FeatureFlagContext.Provider value={baseValue}>{children}</FeatureFlagContext.Provider>;
  }

  const user = buildLaunchDarklyUser();

  return (
    <LDProvider
      clientSideID={clientId}
      reactOptions={{ useCamelCaseFlagKeys: false }}
      options={{ bootstrap: 'localStorage' }}
      user={user}
    >
      <LaunchDarklyBridge overrides={overrides} setOverrides={setOverrides}>
        {children}
      </LaunchDarklyBridge>
    </LDProvider>
  );
}

function LaunchDarklyBridge({ overrides, setOverrides, children }) {
  const ldFlags = useFlags();
  const ldClient = useLDClient();
  const [ready, setReady] = useState(!ldClient);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!ldClient) {
      setReady(true);
      return () => {
        mounted.current = false;
      };
    }
    ldClient
      .waitForInitialization?.()
      .then(() => {
        if (mounted.current) setReady(true);
      })
      .catch(() => {
        if (mounted.current) setReady(true);
      });
    return () => {
      mounted.current = false;
    };
  }, [ldClient]);

  const value = useMemo(
    () => buildFlagContext(DEFAULT_FLAG_VALUES, ldFlags, overrides, ready, setOverrides),
    [ldFlags, overrides, ready, setOverrides],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

function usePersistentOverrides() {
  const [overrides, setOverrides] = useState(() => readOverrides());
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    } catch {
      /* ignore */
    }
  }, [overrides]);
  return [overrides, setOverrides];
}

function readOverrides() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    /* ignore */
  }
  return {};
}

function buildLaunchDarklyUser() {
  const key = ensureUserKey();
  return {
    key,
    anonymous: true,
    custom: {
      platform: 'claimsupplement-web',
      env: import.meta?.env?.MODE || process.env?.NODE_ENV || 'development',
    },
  };
}

function ensureUserKey() {
  if (typeof window === 'undefined') return 'claimsupplement-web';
  try {
    const existing = window.localStorage.getItem(USER_KEY);
    if (existing) return existing;
    const generated = `web-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
    window.localStorage.setItem(USER_KEY, generated);
    return generated;
  } catch {
    return `web-${Date.now()}`;
  }
}

function buildFlagContext(defaults, remoteFlags, overrides, ready, setOverrides) {
  const normalizedRemote = normalizeFlags(remoteFlags);
  const normalizedOverrides = normalizeFlags(overrides);
  const merged = { ...defaults, ...normalizedRemote, ...normalizedOverrides };
  const setOverride = (key, value) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  };
  const resetOverride = (key) => {
    setOverrides((prev) => {
      if (!prev || !(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  return {
    flags: merged,
    ready,
    overrides: normalizedOverrides,
    setOverride,
    resetOverride,
  };
}

function normalizeFlags(flags) {
  if (!flags || typeof flags !== 'object') return {};
  const out = {};
  for (const [key, value] of Object.entries(flags)) {
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
}

export function useFeatureFlags() {
  return useContext(FeatureFlagContext)?.flags ?? DEFAULT_FLAG_VALUES;
}

export function useFeatureFlag(key, fallback) {
  const { flags } = useContext(FeatureFlagContext) || {};
  if (!key) return fallback ?? false;
  if (key in (flags || {})) return flags[key];
  return fallback ?? DEFAULT_FLAG_VALUES[key] ?? false;
}

export function useFeatureFlagControls() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    return {
      flags: DEFAULT_FLAG_VALUES,
      ready: true,
      setOverride: () => {},
      resetOverride: () => {},
    };
  }
  return ctx;
}

export function useFreezeAllowedPath(pathname) {
  const flags = useFeatureFlags();
  return useMemo(() => isAllowedPath(pathname, flags), [pathname, flags]);
}

export { FEATURE_FLAG_KEYS };
